/**
 * Karte der installierten Basis: einzelne Geräte als Punkte in der Farbe ihres Betriebszustands
 * (kritisch, Warnung, Wartung, normal … — Farben wie im Betriebs-Dashboard); beim Herauszoomen fasst
 * ein Kreisdiagramm die Geräte einer Gegend zusammen — Anteile je Betriebszustand, in der
 * Mitte die Anzahl. Wie die Clusterkarte des Portals (map/RenderMarkerSuperCluster.tsx: supercluster
 * + react-minimal-pie-chart), hier auf Mapbox. Klick auf ein Diagramm zoomt hinein, Klick auf einen
 * Punkt zeigt das Gerät.
 */

import { useMemo, useRef, useState, type ReactNode } from "react";
import Map, { Layer, Marker, Source, type MapRef } from "react-map-gl";
import Supercluster from "supercluster";
import { PieChart } from "react-minimal-pie-chart";
import "mapbox-gl/dist/mapbox-gl.css";

import { MAPBOX_TOKEN } from "../utilities/mapbox";

export type InstallationState = "commissioned" | "activated" | "registered";
export type OperatingStateName = "critical" | "warning" | "maintenance" | "normal" | "decommissioned" | "unknown";
export type Point = [id: string, lon: number, lat: number, operating: OperatingStateName, installation: InstallationState];

/** Colours of the operations dashboard (text-destructive, -warning, -success, blue-700, purple-700). */
export const OPERATING_COLOR: Record<OperatingStateName, string> = {
  critical: "#dc2626",
  warning: "#f59e0b",
  maintenance: "#1d4ed8",
  normal: "#16a34a",
  decommissioned: "#7e22ce",
  unknown: "#9ca3af",
};
export const OPERATING_STATES: OperatingStateName[] = ["critical", "warning", "maintenance", "normal", "decommissioned", "unknown"];

export const INSTALLATION_COLOR: Record<InstallationState, string> = {
  commissioned: "#2e7d32",
  activated: "#f9a825",
  registered: "#9e9e9e",
};

/** Below this zoom the devices are shown as pie charts, above it as single points. */
export const CLUSTER_MAX_ZOOM = 14;

type Counts = Record<OperatingStateName, number>;

/** Pie size by number of devices: 18 px for a handful up to 44 px for thousands. */
export const pieRadius = (count: number) => Math.round(Math.min(44, 14 + 6 * Math.log10(Math.max(1, count))));

export const viewFor = (points: Point[]) => {
  if (!points.length) return { longitude: 10.4, latitude: 51.2, zoom: 5 };
  let minLat = Infinity;
  let maxLat = -Infinity;
  let minLon = Infinity;
  let maxLon = -Infinity;
  for (const [, lon, lat] of points) {
    minLat = Math.min(minLat, lat);
    maxLat = Math.max(maxLat, lat);
    minLon = Math.min(minLon, lon);
    maxLon = Math.max(maxLon, lon);
  }
  return {
    bounds: [
      [minLon, minLat],
      [maxLon, maxLat],
    ] as [[number, number], [number, number]],
    fitBoundsOptions: { padding: 40, maxZoom: 14 },
  };
};

export default function InstalledBaseMap(properties: {
  points: Point[];
  onSelectDevice: (id: string) => void;
  children?: ReactNode;
}) {
  const mapRef = useRef<MapRef>(null);
  const [frame, setFrame] = useState<{ bounds: [number, number, number, number]; zoom: number } | null>(null);

  const index = useMemo(() => {
    const cluster = new Supercluster<Counts, Counts>({
      radius: 60,
      maxZoom: CLUSTER_MAX_ZOOM,
      map: (p) => Object.fromEntries(OPERATING_STATES.map((state) => [state, (p as any)[state]])) as Counts,
      reduce: (sum, p) => {
        for (const state of OPERATING_STATES) sum[state] += p[state];
      },
    });
    cluster.load(
      properties.points.map(([id, lon, lat, operating]) => ({
        type: "Feature",
        properties: {
          id,
          state: operating,
          ...Object.fromEntries(OPERATING_STATES.map((state) => [state, state === operating ? 1 : 0])),
        } as any,
        geometry: { type: "Point", coordinates: [lon, lat] },
      })),
    );
    return cluster;
  }, [properties.points]);

  const updateFrame = () => {
    const map = mapRef.current;
    if (!map) return;
    const b = map.getBounds();
    if (!b) return;
    setFrame({ bounds: [b.getWest(), b.getSouth(), b.getEast(), b.getNorth()], zoom: map.getZoom() });
  };

  const { clusters, singles } = useMemo(() => {
    if (!frame) return { clusters: [] as any[], singles: { type: "FeatureCollection", features: [] } as any };
    const all = index.getClusters(frame.bounds, Math.floor(frame.zoom));
    return {
      clusters: all.filter((f: any) => f.properties.cluster),
      singles: { type: "FeatureCollection", features: all.filter((f: any) => !f.properties.cluster) },
    };
  }, [index, frame]);

  return (
    <Map
      ref={mapRef}
      mapboxAccessToken={MAPBOX_TOKEN}
      initialViewState={viewFor(properties.points) as any}
      style={{ width: "100%", height: "100%" }}
      mapStyle="mapbox://styles/mapbox/light-v11"
      interactiveLayerIds={["installedBaseDevices"]}
      onLoad={updateFrame}
      onMoveEnd={updateFrame}
      onClick={(event: any) => {
        const id = event.features?.[0]?.properties?.id;
        if (id) properties.onSelectDevice(String(id));
      }}
    >
      <Source id="installedBaseSingles" type="geojson" data={singles}>
        <Layer
          id="installedBaseDevices"
          type="circle"
          // Critical and warning devices drawn on top of normal ones.
          layout={{ "circle-sort-key": ["match", ["get", "state"], "critical", 3, "warning", 2, "maintenance", 1, 0] as any }}
          paint={{
            "circle-radius": ["interpolate", ["linear"], ["zoom"], 8, 4, 14, 6, 18, 9],
            "circle-color": [
              "match",
              ["get", "state"],
              ...OPERATING_STATES.filter((state) => state !== "unknown").flatMap((state) => [state, OPERATING_COLOR[state]]),
              OPERATING_COLOR.unknown,
            ] as any,
            "circle-stroke-width": 1.5,
            "circle-stroke-color": "#ffffff",
          }}
        />
      </Source>
      {clusters.map((cluster: any) => {
        const [lon, lat] = cluster.geometry.coordinates;
        const count = cluster.properties.point_count as number;
        const r = pieRadius(count);
        const data = OPERATING_STATES.map((state) => ({ value: cluster.properties[state] as number, color: OPERATING_COLOR[state] })).filter(
          (d) => d.value > 0,
        );
        return (
          <Marker key={`cluster-${cluster.id}`} longitude={lon} latitude={lat} anchor="center">
            <button
              type="button"
              aria-label={`${count} Geräte`}
              className="relative cursor-pointer rounded-full border-0 bg-transparent p-0"
              style={{ width: r * 2, height: r * 2 }}
              onClick={(event) => {
                event.stopPropagation();
                const zoom = Math.min(index.getClusterExpansionZoom(cluster.id as number), CLUSTER_MAX_ZOOM + 1);
                mapRef.current?.easeTo({ center: [lon, lat], zoom, duration: 500 });
              }}
            >
              <PieChart data={data} radius={50} lineWidth={45} startAngle={-90} style={{ width: "100%", height: "100%" }} />
              <span
                className="absolute inset-0 flex items-center justify-center font-semibold text-foreground"
                style={{ fontSize: r > 26 ? 12 : 10 }}
              >
                {count >= 1000 ? `${(count / 1000).toFixed(count >= 10000 ? 0 : 1)}k` : count}
              </span>
            </button>
          </Marker>
        );
      })}
      {properties.children}
    </Map>
  );
}
